/**
 * Constants and enums for retreat-pwa.
 * Single source of truth for magic strings.
 */

// Bump this when terms materially change — triggers re-prompt for all users on next open
export const TERMS_VERSION = '2026-06-22' as const;

// External URLs — single source of truth for all outbound links
export const SITE_URLS = {
  WAITLIST: "https://retreatconductor.com/#newsletter",
  TERMS: "https://retreatconductor.com/terms",
  APP: "https://app.retreatconductor.com",
  SUPPORT: "mailto:hello@retreatconductor.com",
} as const;

// LocalStorage / SessionStorage keys
export const STORAGE_KEYS = {
  PERMISSIONS_GRANTED: "rc_permissions_granted",
  PREPARATION_SEEN: "rc_preparation_seen",
  INTENTION_RECORD: "rc_intention_record",
  LAST_USED_SCHEDULE: "rc_last_used_schedule",
  SCHEDULED_RETREAT: "rc_scheduled_retreat",
  REFLECTION: "rc_reflection",
  DEFAULT_BELL: "rc_default_bell",
  TTS_ENABLED: "rc_tts_enabled",
  THEME: "rc_theme",
  ONBOARDING_SEEN: "rc_onboarding_seen",
  ACTIVE_SESSION_ID: "rc_active_session_id",
  SESSION_PREFIX: "rc_session_",
  CONTAINERS_PREFIX: "rc_containers_",
  SCHEDULED_DATETIME: "rc_scheduled_datetime",
  SYNC_QUEUE: "rc_sync_queue",
  INTENTION_PENDING: "rc_intention_pending",
  INTENTION_PENDING_ACTION: "rc_intention_pending_action",
  UI_FLAGS_OVERRIDE: "RC_UI_OVERRIDE",
  BELLS_ENABLED: "rc_bells_enabled",
  WHISPERS_ENABLED: "rc_whispers_enabled",
  PREFERRED_TRADITION: "rc_preferred_tradition",
  AUDIO_REFLECTION_ENABLED: "rc_audio_reflection_enabled",
  LAST_SCREEN: "rc_last_screen",
  SETTINGS_UPDATED_AT: "rc_settings_updated_at",
  SYNC_DEAD: "rc_sync_dead",
  INTENT_LABELS: "rc_intent_labels",
  PRIMARY_INTENT_TAG: "rc_primary_intent_tag",
  WAKE_OFFSET: "rc_wake_offset",
  TRUST_VERIFIED_AT: "rc_trust_verified_at",
  SCHEDULE_HINT_SEEN: "rc_schedule_hint_seen",
  REFLECTION_DRAFT: "rc_reflection_draft",
  SESSION_HISTORY: "rc_session_history",
  PENDING_WAVE: "rc_pending_wave",
  LIBRARY_SEEN: "rc_library_seen",
  HOLD_END_TUTORIAL_SEEN: "rc_hold_end_tutorial_seen",
  PENDING_ALARM_REGISTRATION: "rc_pending_alarm_registration",
  PREFILLED_INVITE_CODE: "rc_prefilled_invite",
  LAST_USER_ID: "rc_last_user_id",
  FEEDBACK_BANNER_DISMISSED: "rc_feedback_banner_dismissed",
  WHISPER_CACHE: "rc_whisper_cache",
  UPDATES_CACHE: "rc_updates_cache",
  USER_SCHEDULES: "rc_user_schedules",
} as const;

// Supabase Edge Function paths
export const EDGE_FN = {
  SCHEDULE_ALARM: "schedule-alarm",
  CANCEL_ALARM: "cancel-alarm",
} as const;

export type WakeOffset = '30min' | '15min' | '5min' | 'self';
export const VALID_WAKE_OFFSETS: WakeOffset[] = ['30min', '15min', '5min', 'self'];
export const WAKE_OFFSET_MS: Record<WakeOffset, number> = {
  '30min': 30 * 60 * 1000,
  '15min': 15 * 60 * 1000,
  '5min':  5 * 60 * 1000,
  'self':  0,
};

// Shell screens that are safe to restore on cold boot.
// Mid-flow screens (live, reflection, intention, etc.) have their own resume
// logic and must not be re-entered directly via stored navigation state.
// 'retreat-detail' restores to 'library' (the schedule object cannot be
// reconstructed from an ID at boot time without coupling to content imports).
export type PersistableScreen = 'home' | 'library' | 'retreat-detail' | 'history' | 'settings';
export const PERSISTABLE_SCREENS: PersistableScreen[] = ['home', 'library', 'retreat-detail', 'history', 'settings'];

// Timing constants (ms)
export const TIMING = {
  DAY_MS: 24 * 60 * 60 * 1000,
  ARMED_WINDOW_MS: 30 * 60 * 1000,
  MISSED_GRACE_MS: 2 * 60 * 60 * 1000,
  // Gesture hold durations — tuned to feel deliberate but not exhausting
  HOLD_TO_BEGIN_MS: 2_000,
  LIVE_END_HOLD_MS: 4_000,       // first gate: surface the end-confirm screen
  END_CONFIRM_HOLD_MS: 3_000,    // second gate: actually stop the engine
  LIVE_HINT_DISPLAY_MS: 8_000,   // how long the gesture hint stays visible
  HOLD_HINT_DISPLAY_MS: 2_000,   // how long the "hold to end" hint stays visible
  BELL_HOLD_DELAY_MS: 500,       // delay before setting default bell
  BELL_RING_DURATION_MS: 3_500,  // how long the bell rings
  BELL_PREVIEW_DELAY_MS: 35,     // delay before playing preview
  COMPLETION_BELL_DELAY_MS: 600, // delay before playing completion bell
  PAYMENT_POLL_INTERVAL_MS: 2_000,
  PAYMENT_ACTIVATED_PAUSE_MS: 1_500,
  AUTH_INIT_TIMEOUT_MS: 8_000,   // safety net: un-block UI if onAuthStateChange never fires
  IDLE_CLOCK_UPDATE_MS: 10_000,  // how often to update wall clock
  AUDIO_CONTEXT_WATCH_MS: 2_000, // audio context watcher interval
  AUDIO_DISPOSE_DELAY_MS: 1_500, // delay before disposing audio context
  AUDIO_SAMPLE_CLEANUP_MS: 8_000, // delay before cleaning up sample audio
  // LiveScreen hold-to-end dim steps
  LIVE_HOLD_STEP1_MS: 1_000,  // first dim: "you're holding"
  LIVE_HOLD_STEP2_MS: 2_500,  // second dim: "almost there"
  // Bell-in-container session transition animation
  BELL_TRANSITION_MS: 2_000,
  // Tab arbitration
  TAB_QUERY_TIMEOUT_MS: 120,     // BroadcastChannel PING/PONG round-trip window
  // Reflection diary
  REFLECTION_AUTOSAVE_DEBOUNCE_MS: 800,     // debounce before writing draft to localStorage
  REFLECTION_DRAFT_SEARCH_WINDOW_MS: 10_000, // fallback: match draft keys within this window of endedAt
  REFLECTION_SAVED_TICKER_MS: 10_000,       // how often the "saved Ns ago" label refreshes
  // HomeScreen
  SCHEDULED_HOME_TICK_MS: 30_000,           // how often the scheduled-home countdown refreshes
  // LiveScreen ghost affordance
  GHOST_REVEAL_AFTER_MS: 600_000,   // 10 minutes: when ghost end-retreat hint appears
  GHOST_VISIBLE_MS: 6_000,           // how long ghost hint is visible before fade
  // Hold-to-end UI dim levels
  HOLD_DIM_STEPS: [0, 0.28, 0.58, 1.0] as const,
  // Time conversion helpers
  HOUR_SECONDS: 60 * 60,             // 3600 seconds per hour
  DAY_SECONDS: 24 * 60 * 60,         // 86400 seconds per day
} as const;

// Server-side error codes returned by the create-checkout edge function.
// Must stay in sync with infra/supabase/functions/create-checkout/index.ts.
export const CHECKOUT_ERROR_CODES = {
  INVALID_CODE: "INVALID_CODE",
  CODE_EXHAUSTED: "CODE_EXHAUSTED",
} as const;

// Supabase auth callback URL parameters — present in query string or hash after token exchange.
export const AUTH_URL_PARAMS = {
  TOKEN_HASH: "token_hash",
  CODE: "code",
  TYPE: "type",
} as const;

// Application URL parameters — consumed on landing and cleared after reading.
export const APP_URL_PARAMS = {
  SESSION_ID: "session_id",
  TOKEN: "token",
  INVITE: "invite",
  WAVE: "wave",
} as const;

// Theme values
export const THEMES = {
  LIGHT: "light",
  DARK: "dark",
} as const;

export type Theme = typeof THEMES[keyof typeof THEMES];

// Session status values — mirrors RetreatSession.status type
export const SESSION_STATUS = {
  COMPLETED: 'completed',
  INCOMPLETE: 'incomplete',
  IN_PROGRESS: 'in_progress',
} as const;

// Intention kinds
export const INTENTION_KINDS = {
  UNSET: "unset",
  DECLINED: "declined",
  PRESET: "preset",
  CUSTOM: "custom",
} as const;

// Whisper contexts
export const WHISPER_CONTEXTS = {
  HOME_GREETING: "home.greeting",
  PREFLIGHT_FOOTER: "preflight.footer",
  CLOSE_SUBTITLE: "close.subtitle",
} as const;

// Date/time formats
export const DATE_FORMATS = {
  WEEKDAY_LONG: "long",
  MONTH_LONG: "long",
  DAY_NUMERIC: "numeric",
  HOUR_2DIGIT: "2-digit",
  MINUTE_2DIGIT: "2-digit",
  HOUR12_FALSE: false,
} as const;

// File/upload limits
export const LIMITS = {
  PHOTO_MAX_MB: 4,
  PHOTO_MAX_BYTES: 4 * 1024 * 1024,
  // Database query limits
  DB_SESSION_LIMIT: 200,           // max sessions per query
  DB_CONTAINER_LIMIT: 2000,        // max containers per query
  DB_CHUNK_LIMIT: 500,             // max chunk records per query
  // Sync queue
  SYNC_QUEUE_MAX_SIZE: 500,        // max pending sync operations
  // Gestures
  VIBRATE_FEEDBACK_MS: 160,        // haptic feedback duration
  GESTURE_TIMEOUT_MS: 2000,        // trust gesture timeout
} as const;

// Schedule facet thresholds (in seconds)
export const SCHEDULE_FACETS = {
  UNDER_HOUR_MAX_SECONDS: 1 * 60 * 60,     // 3600s
  HALF_DAY_MAX_SECONDS: 5 * 60 * 60,       // 18000s
} as const;

// Intention display limits
export const INTENTION_DISPLAY_LIMITS = {
  MAX_CUSTOM_TEXT_CHARS: 60,    // UI card width constraint
  MAX_LABEL_COUNT: 10,          // max intention tags per session
  LABEL_TRUNCATION: '…',        // ellipsis for truncated text
} as const;

// Supabase Storage bucket names — single source of truth across service layer
export const STORAGE_BUCKETS = {
  FEEDBACK_AUDIO: "feedback-audio",
  FEEDBACK_SCREENSHOTS: "feedback-screenshots",
  FEEDBACK_LOGS: "feedback-logs",
  REFLECTION_PHOTOS: "reflection-photos",
} as const;

// Session kinds
export const SESSION_KINDS = {
  SLEEP: "Sleep",
  REST: "Rest",
  MEDITATION: "Meditation",
  WALKING: "Walking",
} as const;

// Tradition branding colors — single source of truth
export const TRADITION_COLORS = {
  zen: "#6f7d5e",
  vipassana: "#9a6f53",
  secular: "#6f7c86",
} as const;

// Display modes
export const DISPLAY_MODES = {
  STANDALONE: "standalone",
} as const;

// Tab coordination message types
export const TAB_MSG_TYPES = {
  PING: "PING",
  PONG: "PONG",
  OWNERSHIP_TRANSFER: "OWNERSHIP_TRANSFER",
  END_RETREAT: "END_RETREAT",
} as const;

// UI action types
export const UI_ACTION_TYPES = {
  TAKE_OWNERSHIP: "TAKE_OWNERSHIP",
  RETREAT_ENDED_GLOBALLY: "RETREAT_ENDED_GLOBALLY",
  SHOW_END_CONFIRM: "SHOW_END_CONFIRM",
  CANCEL_END: "CANCEL_END",
  SHOW_COMPLETION: "SHOW_COMPLETION",
  DISMISS_COMPLETION: "DISMISS_COMPLETION",
  PERMISSIONS_GRANTED: "PERMISSIONS_GRANTED",
  PERMISSIONS_DISMISSED: "PERMISSIONS_DISMISSED",
  PREPARATION_SEEN: "PREPARATION_SEEN",
  BOOT_COMPLETE: "BOOT_COMPLETE",
  RESET: "RESET",
  OPEN_SELECTION: "OPEN_SELECTION",
  SELECT_RETREAT: "SELECT_RETREAT",
  CONFIRM_SCHEDULE: "CONFIRM_SCHEDULE",
  CANCEL_SCHEDULING: "CANCEL_SCHEDULING",
  CANCEL_SCHEDULED_RETREAT: "CANCEL_SCHEDULED_RETREAT",
  LOAD_SCHEDULED_RETREAT: "LOAD_SCHEDULED_RETREAT",
  BEGIN_SCHEDULED_RETREAT: "BEGIN_SCHEDULED_RETREAT",
  ENTER_ARMED: "ENTER_ARMED",
  ENTER_MISSED: "ENTER_MISSED",
  SHOW_WAKE_UP_CONTAINER: "SHOW_WAKE_UP_CONTAINER",
  DISMISS_WAKE_UP_CONTAINER: "DISMISS_WAKE_UP_CONTAINER",
  OPEN_LIBRARY: "OPEN_LIBRARY",
  VIEW_RETREAT_DETAIL: "VIEW_RETREAT_DETAIL",
  CLOSE_LIBRARY: "CLOSE_LIBRARY",
  START_NOW: "START_NOW",
  SCHEDULE_FROM_DETAIL: "SCHEDULE_FROM_DETAIL",
  SHOW_INSTALL_BRIDGE: "SHOW_INSTALL_BRIDGE",
  SHOW_INSTALL_PROMPT: "SHOW_INSTALL_PROMPT",
  DISMISS_INSTALL: "DISMISS_INSTALL",
  OPEN_HISTORY: "OPEN_HISTORY",
  CLOSE_HISTORY: "CLOSE_HISTORY",
  OPEN_SETTINGS: "OPEN_SETTINGS",
  CLOSE_SETTINGS: "CLOSE_SETTINGS",
  OPEN_BELL_LIBRARY: "OPEN_BELL_LIBRARY",
  PREVIEW_BELL: "PREVIEW_BELL",
  CLOSE_BELL_PREVIEW: "CLOSE_BELL_PREVIEW",
  CLOSE_BELL_LIBRARY: "CLOSE_BELL_LIBRARY",
  SHOW_REFLECTION: "SHOW_REFLECTION",
  DISMISS_REFLECTION: "DISMISS_REFLECTION",
  SET_INTENTION: "SET_INTENTION",
  CLEAR_INTENTION: "CLEAR_INTENTION",
  SHOW_END_INTENTION: "SHOW_END_INTENTION",
  DISMISS_END_INTENTION: "DISMISS_END_INTENTION",
  SHOW_SCHEDULE_OVERLAY: "SHOW_SCHEDULE_OVERLAY",
  HIDE_SCHEDULE_OVERLAY: "HIDE_SCHEDULE_OVERLAY",
  RETURNING_START: "RETURNING_START",
  RETURNING_COMPLETE: "RETURNING_COMPLETE",
  OTHER_TAB_ACTIVE: "OTHER_TAB_ACTIVE",
  OTHER_TAB_TAKEOVER: "OTHER_TAB_TAKEOVER",
} as const;

// Screen names
export const SCREENS = {
  BOOTING: "booting",
  PERMISSIONS: "permissions",
  INSTALL_BRIDGE: "install-bridge",
  INSTALL_PROMPT: "install-prompt",
  SELECTION: "selection",
  LIBRARY: "library",
  RETREAT_DETAIL: "retreat-detail",
  SCHEDULING: "scheduling",
  HOME_SCHEDULED: "home-scheduled",
  WAITING: "waiting",
  MISSED: "missed",
  PREPARATION: "preparation",
  INTENTION: "intention",
  RETURNING: "returning",
  HOME: "home",
  WAKE_UP_CONTAINER: "wake-up-container",
  LIVE: "live",
  END_CONFIRM: "end-confirm",
  END_INTENTION: "end-intention",
  COMPLETION: "completion",
  REFLECTION: "reflection",
  HISTORY: "history",
  SETTINGS: "settings",
  BELL_LIBRARY: "bell-library",
  BELL_PREVIEW: "bell-preview",
} as const;
